import { Building2, User as UserIcon, Sparkles } from 'lucide-react';
import type { CompanyDraft, FieldSource } from '../../../lib/mail/companyProspect';
import { SegmentedControl } from './DrawerControls';

export type CreateKind = 'person' | 'company' | 'both';

export interface PersonValues {
  business_name: string;
  email: string;
  phone: string;
  contact_role: string;
}

interface Props {
  kind: CreateKind;
  onKindChange: (k: CreateKind) => void;
  company: Record<string, string>;
  companyDraft: CompanyDraft;
  onCompanyChange: (field: string, value: string) => void;
  person: PersonValues;
  onPersonChange: (field: keyof PersonValues, value: string) => void;
  pipelines: { id: string; name: string }[];
  pipelineId: string;
  onPipelineChange: (id: string) => void;
}

const SOURCE_LABEL: Record<FieldSource, string> = {
  vies: 'VIES',
  website: 'sito',
  signature: 'firma',
  domain: 'dominio',
  manual: '',
};

const KINDS: { key: CreateKind; label: string }[] = [
  { key: 'person', label: 'Solo persona' },
  { key: 'company', label: 'Solo azienda' },
  { key: 'both', label: 'Azienda + persona' },
];

const inputClass =
  'w-full px-3 py-2 text-sm bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-transparent rounded-lg transition-all duration-150 focus:outline-none focus:bg-white dark:focus:bg-gray-800 focus:border-[#2383E2] focus:ring-2 focus:ring-[#2383E2]/15 text-[#37352F] dark:text-white placeholder:text-[#9B9A97]';

/** Intestazione di sezione con icona, allineata al resto del pannello. */
function SectionTitle({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-semibold text-[#787774] dark:text-white/50">
      {icon}{children}
    </div>
  );
}

/**
 * Etichetta di provenienza del valore: distingue a colpo d'occhio un dato trovato
 * in automatico da uno scritto a mano.
 */
function SourceBadge({ source }: { source: FieldSource }) {
  if (!SOURCE_LABEL[source]) return null;
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-[rgba(35,131,226,0.1)] text-[#2383E2] font-medium"
      title="Valore trovato in automatico: modificalo pure, non verra' piu' sovrascritto"
    >
      <Sparkles className="h-2.5 w-2.5" />
      {SOURCE_LABEL[source]}
    </span>
  );
}

export function CreateEntityForm(props: Props) {
  const { kind, onKindChange, company, companyDraft, onCompanyChange, person, onPersonChange } = props;
  const showCompany = kind === 'company' || kind === 'both';
  const showPerson = kind === 'person' || kind === 'both';

  const field = (
    label: string,
    name: string,
    value: string,
    onChange: (v: string) => void,
    source?: FieldSource,
    placeholder?: string,
  ) => (
    <div key={name}>
      <div className="flex items-center justify-between mb-1 min-h-[18px]">
        <label className="text-xs font-medium text-[#787774] dark:text-white/40">{label}</label>
        {source && <SourceBadge source={source} />}
      </div>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        className={inputClass}
      />
    </div>
  );

  return (
    <div className="space-y-4">
      <SegmentedControl items={KINDS} active={kind} onChange={onKindChange} />

      {showCompany && (
        <div className="space-y-3 mail-link-section">
          <SectionTitle icon={<Building2 className="h-3 w-3" />}>Azienda</SectionTitle>
          {field('Ragione sociale', 'business_name', company.business_name || '', v => onCompanyChange('business_name', v), companyDraft.business_name?.source, 'Nome legale dell\'azienda')}
          {field('Sito web', 'website', company.website || '', v => onCompanyChange('website', v), companyDraft.website?.source, 'esempio.it')}
          {field('Partita IVA', 'vat_number', company.vat_number || '', v => onCompanyChange('vat_number', v), companyDraft.vat_number?.source, '11 cifre')}
          {field('Telefono', 'company_phone', company.phone || '', v => onCompanyChange('phone', v), companyDraft.phone?.source, 'Centralino')}
          {field('Citta', 'city', company.city || '', v => onCompanyChange('city', v), companyDraft.city?.source)}
        </div>
      )}

      {showPerson && (
        <div className="space-y-3 mail-link-section">
          <SectionTitle icon={<UserIcon className="h-3 w-3" />}>
            {kind === 'both' ? 'Persona di riferimento' : 'Persona'}
          </SectionTitle>
          {field('Nome', 'person_name', person.business_name, v => onPersonChange('business_name', v), undefined, 'Nome e cognome')}
          {field('Email', 'person_email', person.email, v => onPersonChange('email', v))}
          {field('Telefono', 'person_phone', person.phone, v => onPersonChange('phone', v), undefined, 'Cellulare')}
          {field('Ruolo', 'person_role', person.contact_role, v => onPersonChange('contact_role', v), undefined, 'Es. Responsabile acquisti')}
        </div>
      )}

      {/* La pipeline si applica sempre all'entita' che diventa lead: l'azienda se c'e',
          altrimenti la persona. Il referente resta comunque senza pipeline. */}
      <div>
        <label className="text-xs font-medium text-[#787774] dark:text-white/40 mb-1 block">Pipeline (opzionale)</label>
        <select value={props.pipelineId} onChange={e => props.onPipelineChange(e.target.value)} className={inputClass}>
          <option value="">Predefinita</option>
          {props.pipelines.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
    </div>
  );
}
